import {gridToWorld, PIXELS_PER_METER} from "@/dungeons/geometry/Coordinates.ts";
import type {LevelGeometryDocument, LightAnimation, WorldPoint} from "@/dungeons/model/DungeonGeometry.ts";
import type {LitArea} from "./VisibilityModel.ts";
import {type Segment, type VisibilityPolygon, visibilityPolygon} from "./VisibilityGeometry.ts";
import type {EncounterLights} from "@/encounter/lights/EncounterLights.ts";
import type Combatant from "@/encounter/model/Combatant.ts";
import {DEFAULT_LIGHT_COLOR_STRENGTH} from "@/dungeons/lighting/LightPresets.ts";

export type EffectiveLight = {id: string; source: "dungeon" | "encounter"; origin: WorldPoint;
    rangeMeters: number; intensity: number; colorStrength?: number; color: string; enabled: boolean; preset: string;
    combatantId?: string | null; animation?: LightAnimation | null};

export function effectiveLights(geometry: LevelGeometryDocument, levelId: string, state: EncounterLights,
    combatants: Combatant[]): EffectiveLight[] {
    const overrides = new Map(state.overrides.map(item => [item.lightId, item]));
    const dungeon = geometry.lights.map(light => {
        const override = overrides.get(light.id);
        return {...light, source: "dungeon" as const, origin: override?.position ?? gridToWorld(light.position, geometry.grid),
            enabled: override?.enabled ?? light.enabled, rangeMeters: override?.rangeMeters ?? light.rangeMeters,
            intensity: override?.intensity ?? light.intensity,
            colorStrength: override?.colorStrength ?? light.colorStrength ?? DEFAULT_LIGHT_COLOR_STRENGTH,
            color: override?.color ?? light.color,
            animation: override ? override.animation ?? null : light.animation ?? null};
    });
    const encounter = state.lights.flatMap(light => {
        const combatant = combatants.find(item => item.id === light.combatantId);
        if ((combatant?.levelId ?? light.levelId) !== levelId) return [];
        return [{...light, source: "encounter" as const, origin: combatant?.position ?? light.position}];
    });
    return [...dungeon, ...encounter];
}

export function lightAreas(lights: EffectiveLight[], blockers: readonly Segment[],
    polygonCache?: Map<string, VisibilityPolygon>, blockerKey = ""): LitArea[] {
    return lights.filter(light => light.enabled && light.intensity > 0).map(light => {
        // Door changes alter the polygon even when the light itself is unchanged.
        const key = `${blockerKey}:${light.id}:${light.origin.x}:${light.origin.y}:${light.rangeMeters}`;
        let polygon = polygonCache?.get(key);
        if (!polygon) {
            polygon = visibilityPolygon(light.origin, blockers, light.rangeMeters * PIXELS_PER_METER);
            if (polygonCache) {
                if (polygonCache.size >= 512) polygonCache.clear();
                polygonCache.set(key, polygon);
            }
        }
        return {id: light.id, animation: light.animation, origin: light.origin, polygon, radiusMeters: light.rangeMeters,
            intensity: light.intensity, colorStrength: light.colorStrength ?? DEFAULT_LIGHT_COLOR_STRENGTH,
            color: light.color};
    }).filter(light => light.polygon.length >= 3);
}

/** Uses the same wall and effective door blockers as token vision. */
export function dungeonLightAreas(geometry: LevelGeometryDocument, blockers: readonly Segment[],
    polygonCache?: Map<string, VisibilityPolygon>, blockerKey = ""): LitArea[] {
    return lightAreas(geometry.lights.map(light => ({...light, source: "dungeon", origin: gridToWorld(light.position, geometry.grid)})),
        blockers, polygonCache, blockerKey);
}
